/* eslint-disable no-unused-expressions */
import React, { useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { displayToast } from '../../Util/Toast';
import { postBlogArticle } from '../../Util/ApiService';
import BlogArticleModalView, {
  BLOG_ARTICLE_DRAFT_KEY
} from '../../Components/BlogArticleAdmin/BlogArticleModal';

const DEFAULT_LANGUAGE_KEY = 'default';

const showModalWrapper = () => {
  const wrapper = window.document.querySelector('.modal-wrapper');
  if (wrapper) wrapper.classList.remove('hidden');
};

const BlogArticleAdminModal = ({
  addBlogArticleUri = '',
  updateBlogArticleUri = '',
  updateTranslationUri = '',
  type = 'add',
  preFetchedBlogArticle = null,
  availableLanguages = [],
  translations = {},
  imageUrl = null,
  imageAltText = '',
  categories = [],
  authors = [],
  sites = [],
  blogTagSearchUri = '',
  mostUsedTags = []
}) => {
  const [posting, setPosting] = useState(false);
  const [mode, setMode] = useState(type);
  const [article, setArticle] = useState(preFetchedBlogArticle);
  const [updateUri, setUpdateUri] = useState(updateBlogArticleUri);
  const [selectedLanguage, setSelectedLanguage] =
    useState(DEFAULT_LANGUAGE_KEY);
  const [translationsState, setTranslationsState] = useState(translations);
  const [updateTranslationUriState, setUpdateTranslationUriState] = useState(updateTranslationUri);
  const [imageUrlState, setImageUrlState] = useState(imageUrl);
  const [imageAltTextState, setImageAltTextState] = useState(imageAltText);
  const isEdit = mode === 'edit';
  const isTranslation = isEdit && selectedLanguage !== DEFAULT_LANGUAGE_KEY;
  const urlSlugReadOnly = isTranslation && availableLanguages.some((language) => {
    return language.code === selectedLanguage && language.keepsEnglishSlug;
  });

  useEffect(() => {
    window.openBlogArticleModal = (payload = {}) => {
      setMode(payload.mode === 'edit' ? 'edit' : 'add');
      setArticle(payload.article || null);
      setUpdateUri(payload.updateUri || updateBlogArticleUri);
      setUpdateTranslationUriState(
        payload.updateTranslationUri || updateTranslationUri
      );
      setTranslationsState(payload.translations || {});
      setImageUrlState(payload.imageUrl || null);
      setImageAltTextState(payload.imageAltText || '');
      setSelectedLanguage(DEFAULT_LANGUAGE_KEY);
      showModalWrapper();
    };
    return () => {
      delete window.openBlogArticleModal;
    };
  }, [updateBlogArticleUri, updateTranslationUri]);

  let currentContent;
  if (!isEdit) {
    currentContent = article;
  } else {
    currentContent =
      translationsState[selectedLanguage] ||
      (selectedLanguage === DEFAULT_LANGUAGE_KEY ? article : {}) ||
      {};

    if (selectedLanguage === DEFAULT_LANGUAGE_KEY) {
      // the author and the categories belong to the article, not to a translation of it
      currentContent = {
        ...currentContent,
        categoryIds: article?.categoryIds || [],
        blogAuthorId: article?.blogAuthorId || null,
        sitesCodes: article?.sitesCodes || []
      };
    }
  }

  if (urlSlugReadOnly) {
    currentContent = {
      ...currentContent,
      urlSlug: translationsState.default?.urlSlug || article?.urlSlug || ''
    };
  }

  const handleSubmit = async (payload) => {
    setPosting(true);

    const formData = new FormData();
    formData.append('title', payload.title);
    formData.append('subTitle', payload.subTitle);
    formData.append('body', payload.body || '');
    formData.append('urlSlug', payload.urlSlug);
    formData.append('seoTitle', payload.seoTitle || '');
    formData.append('seoDescription', payload.seoDescription || '');
    formData.append('seoKeywords', payload.seoKeywords || '');

    let targetUri;
    if (isTranslation) {
      formData.append('languageCode', selectedLanguage);
      targetUri = updateTranslationUriState;
    } else {
      formData.append('blogAuthorId', payload.blogAuthorId || '');
      formData.append('imageAltText', payload.imageAltText || '');
      formData.append('imageUpdated', payload.imageChanged);
      if (payload.image) {
        formData.append('image', payload.image, payload.image.name);
      }
      formData.append('categoryIds', JSON.stringify(payload.categoryIds || []));
      formData.append('tags', JSON.stringify(payload.tags || []));
      formData.append('sitesCodes', JSON.stringify(payload.sitesCodes || []));
      targetUri = isEdit ? updateUri : addBlogArticleUri;
    }

    await postBlogArticle(
      targetUri,
      formData,
      () => {
        if (!isEdit) {
          window.sessionStorage.removeItem(BLOG_ARTICLE_DRAFT_KEY);
        }
        displayToast(
          isEdit ? 'Blog article saved' : 'Blog article created',
          'success'
        );
        window.location.reload();
      },
      () => {
        setPosting(false);
        displayToast(
          isEdit ? 'Error saving blog article' : 'Error creating blog article',
          'error'
        );
      }
    );
  };

  const handleCategoryChange = (categoryIds) => {
    setArticle((currentArticle) => {
      return {
        ...(currentArticle || {}),
        categoryIds
      };
    });
  };

  return (
    <BlogArticleModalView
      key={isEdit ? `edit-${updateUri}-${selectedLanguage}` : 'add'}
      posting={posting}
      onSubmit={handleSubmit}
      type={mode}
      blogArticle={currentContent}
      isTranslation={isTranslation}
      urlSlugReadOnly={urlSlugReadOnly}
      selectedLanguage={selectedLanguage}
      availableLanguages={availableLanguages}
      categories={categories}
      authors={authors}
      sites={sites}
      translations={translationsState}
      onLanguageChange={setSelectedLanguage}
      onCategoryChange={handleCategoryChange}
      blogTagSearchUri={blogTagSearchUri}
      mostUsedTags={mostUsedTags}
      imageUrl={imageUrlState}
      imageAltText={imageAltTextState}
    />
  );
};

export default BlogArticleAdminModal;

BlogArticleAdminModal.propTypes = {
  addBlogArticleUri: PropTypes.string,
  updateBlogArticleUri: PropTypes.string,
  updateTranslationUri: PropTypes.string,
  type: PropTypes.oneOf(['add', 'edit']),
  preFetchedBlogArticle: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    body: PropTypes.string,
    author: PropTypes.string,
    urlSlug: PropTypes.string,
    seoTitle: PropTypes.string,
    seoDescription: PropTypes.string,
    seoKeywords: PropTypes.string,
    categoryIds: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    sitesCodes: PropTypes.arrayOf(PropTypes.string),
    tags: PropTypes.arrayOf(PropTypes.string)
  }),
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
      keepsEnglishSlug: PropTypes.bool
    })
  ),
  translations: PropTypes.object,
  imageUrl: PropTypes.string,
  imageAltText: PropTypes.string,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      depth: PropTypes.number.isRequired
    })
  ),
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  sites: PropTypes.arrayOf(
    PropTypes.shape({
      siteCode: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  blogTagSearchUri: PropTypes.string,
  mostUsedTags: PropTypes.arrayOf(PropTypes.string)
};
